import React, { useState, useEffect } from 'react';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialMode?: 'login' | 'signup' | 'forgot';
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'login' }) => {
    const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(initialMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [missingFieldsError, setMissingFieldsError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setMode(initialMode);
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setName('');
            setAddress('');
            setCity('');
            setState('');
            setZipCode('');
            setMessage(null);
            setIsSuccess(false);
            setLoading(false);
            setMissingFieldsError(null);
        }
    }, [isOpen, initialMode]);

    if (!isOpen) return null;

    const changeMode = (newMode: 'login' | 'signup' | 'forgot') => {
        setMessage(null);
        setIsSuccess(false);
        setMode(newMode);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // setLoading(true);
        setMissingFieldsError(null);
        setMessage(null);
        setIsSuccess(false);
        // 1. Validation Logic
        const missing: string[] = [];
        if (mode === 'login') {
            if (!email.trim()) missing.push('email');
            if (!password.trim()) missing.push('password');
        } else if (mode === 'signup') {
            if (!name.trim()) missing.push('full name');
            if (!email.trim()) missing.push('email');
            if (!password.trim()) missing.push('password');
            if (!confirmPassword.trim()) missing.push('confirm password');
        } else if (mode === 'forgot') {
            if (!email.trim()) missing.push('email');
        }

        if (missing.length > 0) {
            // Replicates text output format: "Please enter both email and password"
            const fieldText = missing.join(' , ');
            setMissingFieldsError(`Please enter  ${fieldText}`);
            return;
        }

        setLoading(true);

        try {
            if (mode === 'login') {
                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password }),
                });
                const data = await response.json();

                if (!response.ok) throw new Error(data.message || data.error || 'Invalid credentials');

                const mongoUserId = data.user?.id || data.user?.userId || data.user?._id;

                const sessionWrapper = {
                    user: { 
                        id: mongoUserId,
                        userId: mongoUserId,
                        _id: mongoUserId,
                        email: data.user?.email, 
                        user_metadata: { 
                            full_name: data.user?.name || '', 
                            address: data.user?.address || '' 
                        } 
                    },
                    token: data.token
                };
                
                localStorage.setItem('mitifresh_session', JSON.stringify(sessionWrapper));
                window.dispatchEvent(new Event('mitifresh-auth-change'));

                onClose();
                window.location.hash = '#/garden';

            } else if (mode === 'signup') {
                if (password !== confirmPassword) {
                    throw new Error("Passwords do not match.");
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name, email, password, address, city, state, zipCode, coordinates }),
                });
                const data = await response.json();

                if (!response.ok) throw new Error(data.message || data.error || 'Registration failed');

                const mongoUserId = data.user?.id || data.user?.userId || data.user?._id;

                const sessionWrapper = {
                    user: {
                        id: mongoUserId,
                        userId: mongoUserId,
                        _id: mongoUserId,
                        email: data.user?.email,
                        user_metadata: {
                            full_name: data.user?.name || '',
                            address: data.user?.address || ''
                        }
                    },
                    token: data.token
                };

                localStorage.setItem('mitifresh_session', JSON.stringify(sessionWrapper));
                window.dispatchEvent(new Event('mitifresh-auth-change'));

                onClose();
                window.location.hash = '#/garden';

            } else if (mode === 'forgot') {
                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/forgot-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                const data = await response.json();

                if (!response.ok) throw new Error(data.message || data.error || 'Failed to dispatch verification email');

                setIsSuccess(true);
                setMessage('Password reset instructions have been dispatched.');
            }
        } catch (err: any) {
            setMessage(err.message || 'An operational error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full relative shadow-2xl border border-gray-100 my-8 animate-in fade-in zoom-in-95 duration-200">
                <button onClick={onClose} className="absolute right-4 top-4 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <h2 className="text-2xl font-bold mb-5 text-gray-950" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                    {mode === 'login' && 'Log in'}
                    {mode === 'signup' && 'Create Account'}
                    {mode === 'forgot' && 'Reset your password'}
                </h2>

                {message && (
                    <div className={`p-3 rounded-lg text-sm mb-4 ${isSuccess ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                        {message}
                    </div>
                )}

                {!isSuccess && (
                    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                        {mode === 'signup' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none focus:ring-2 focus:ring-green-500" />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>

                        {mode !== 'forgot' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none focus:ring-2 focus:ring-green-500" />
                                </div>
                                {mode === 'signup' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none focus:ring-2 focus:ring-green-500" />
                                    </div>
                                )}
                            </div>
                        )}

                        {mode === 'signup' && (
                            <div className="space-y-4 border-t border-gray-100 pt-3">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Address Details (Optional)</p>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none focus:ring-2 focus:ring-green-500" />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-1">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                                        <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Rourkela" className="w-full px-2 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none" />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                        <input type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="Odisha" className="w-full px-2 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none" />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">ZIP Code</label>
                                        <input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="769008" className="w-full px-2 py-2 border border-gray-300 rounded-lg text-black bg-white focus:outline-none" />
                                    </div>
                                </div>
                            </div>
                        )}

                        <button type="submit" disabled={loading} className="w-full mt-2 py-2.5 rounded-lg text-white font-medium transition-colors bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
                            {loading ? 'Processing...' : mode === 'login' ? 'Log in' : mode === 'signup' ? 'Register' : 'Send reset link'}
                        </button>
                    </form>
                )}

                <div className="mt-5 pt-4 border-t border-gray-100 text-center text-sm text-gray-600 flex flex-col gap-2">
                    {mode === 'login' && (
                        <>
                            <p>Need an account? <button type="button" onClick={() => changeMode('signup')} className="text-green-600 font-medium hover:underline">Sign up</button></p>
                        </>
                    )}
                    {mode === 'signup' && (
                        <p>Already have an account? <button type="button" onClick={() => changeMode('login')} className="text-green-600 font-medium hover:underline">Log in</button></p>
                    )}
                    {mode === 'forgot' && (
                        <button type="button" onClick={() => changeMode('login')} className="text-green-600 font-medium hover:underline block mx-auto">Back to log in</button>
                    )}
                </div>
            {/* Custom Validation Overlay Modal */}
                {missingFieldsError && (
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4 rounded-2xl animate-in fade-in duration-100">
                        <div className="bg-[#333333] text-white rounded-xl p-6 max-w-xs w-full shadow-2xl text-left border border-gray-700/50">
                            <h3 className="text-xl font-semibold mb-2 text-white" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                                Error
                            </h3>
                            <p className="text-gray-200 text-sm mb-6 leading-relaxed" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                                {missingFieldsError}
                            </p>
                            <div className="text-right">
                                <button 
                                    type="button" 
                                    onClick={() => setMissingFieldsError(null)} 
                                    className="text-emerald-400 font-bold hover:text-emerald-300 px-3 py-1 transition-colors text-sm uppercase tracking-wider"
                                    style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthModal;